import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import RecursiveMenu from "./recursiveMenu";
import { SidebarItem } from "../../types/sidebar";
import * as Icons from "../../assets/SVGs/index";
import { RootState } from "../../state-management/store";
import ToggleChangeTheme from "../../components/toggle-change-theme";
import theme from "../../config/theme";

interface SidebarProps {
  data: SidebarItem[];
}

const Sidebar: React.FC<SidebarProps> = ({ data }) => {
  const themeMode = useSelector((state: RootState) => state.globalState.theme);
  const mode = themeMode === "light" ? theme.light : theme.dark;

  const [isCollapsed, setIsCollapsed] = useState<boolean>(() =>
    JSON.parse(localStorage.getItem("isCollapsed") || "false")
  );
  const [isVisible, setIsVisible] = useState(false); // For mobile visibility
  const [isMobile, setIsMobile] = useState<boolean>(false); // Check for mobile view
  const [scrolled, setScrolled] = useState(false); // Track scroll position

  const handleToggleCollapse = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsCollapsed((prev) => {
      const newState = !prev;
      localStorage.setItem("isCollapsed", JSON.stringify(newState));
      return newState;
    });
  };

  const handleToggleVisibility = () => setIsVisible((prev) => !prev);

  // Handle closing the sidebar when clicked outside
  const handleClickOutside = useCallback((event: MouseEvent) => {
    const sidebarElement = document.getElementById("sidebar");
    if (sidebarElement && !sidebarElement.contains(event.target as Node)) {
      setIsVisible(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  // Detect screen size for mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setIsMobile(true); // Set mobile view
      } else {
        setIsMobile(false); // Set desktop view
      }
    };

    handleResize(); // Check on mount
    window.addEventListener("resize", handleResize); // Update on resize

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (isMobile) {
      setIsCollapsed(true); // Always collapsed on mobile
    }
  }, [isMobile]);

  // Detect scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);  // Change style after scrolling 50px
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <div>
      {/* Mobile Hamburger Button with Scroll Effect */}
      <button
        className={`fixed top-4 right-4 z-20 p-2 rounded-full lg:hidden transition-all duration-300 
          ${scrolled ? 'bg-[#333333] text-white shadow-lg' : 'text-[#777777] bg-transparent'}`}
        onClick={handleToggleVisibility}
      >
        <Icons.HamburgerIcon className="w-6 h-6" />
      </button>

      {/* Overlay Background */}
      {isVisible && (
        <div
          className="fixed top-0 left-0 right-0 bottom-0 bg-black opacity-50 z-10"
          onClick={() => setIsVisible(false)} // Close sidebar when overlay clicked
        />
      )}

      {/* Sidebar */}
      <div
        id="sidebar"
        className={`fixed top-0 right-0 h-full z-20 transition-transform duration-300 ${isVisible || !isCollapsed ? "translate-x-0" : "translate-x-full"} ${isCollapsed ? "w-[46px]" : "w-[250px]"} items-start bg-gray-900 shadow-md flex flex-col justify-between lg:translate-x-0`}
        style={{ backgroundColor: mode.backgroundColor.bgTertiary }}
      >
        {/* Toggle Collapse Button */}
        <button
          onClick={handleToggleCollapse}
          className="p-2 self-end focus:outline-none rounded hidden md:block"
        >
          {isCollapsed ? (
            <Icons.ArrowLeftIcon className="w-6 h-6 text-white" />
          ) : (
            <Icons.ArrowRightIcon className="w-6 h-6 text-white" />
          )}
        </button>

        {/* Menu Items */}
        <div className="flex-grow overflow-y-auto w-full">
          <RecursiveMenu
           items={data}
           level={0}
           parentIndex={[]}
           isCollapsed={isCollapsed}
           submenuBgColor={mode.backgroundColor.bgTertiaryTrans}
          />
        </div>

        {/* Footer with Theme Toggle */}
     <div className="w-full justify-center pb-2">
     <div className="flex justify-around mt-4">
                <ToggleChangeTheme
                    lightIcon={
                        <Icons.MoonIcon
                            fill="#000000"
                            className="absolute bottom-[6%] left-[0%] h-6 w-6 rounded-full bg-[#ffffff] transition-all duration-500 peer-checked:transform peer-checked:translate-x-full"
                        />
                    }
                    darkIcon={
                        <Icons.SunIcon
                            fill="#ffffff"
                            className="absolute bottom-[6%] left-[-20%] h-6 w-6 rounded-full bg-[#25282D] transition-all duration-500 peer-checked:transform peer-checked:translate-x-full"
                        />
                    }
                />
            </div>
     </div>
      </div>

      {/* Push Content Left */}
      <div className={`transition-all duration-300 ${isCollapsed ? "ml-0" : "mr-[290px]"}`}>
        {/* Main Content */}
      </div>
    </div>
  );
};

export default Sidebar;
